import React, { useState, useEffect } from 'react';
import { startProCheckout } from '../utils/billing';
import { Container, Row, Col, Card, Spinner, Alert, Button, Form, InputGroup, Badge, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaSearch, FaArrowLeft, FaFileAlt, FaStar, FaCrown, FaLock, FaShieldAlt, FaChartBar, FaBuilding, FaServer, FaCog, FaExternalLinkAlt, FaMagic } from 'react-icons/fa';
import { BiCheckCircle } from 'react-icons/bi';
import { getAvailableTemplates, getTemplatesForScan, normalizeTemplateId } from '../services/templateService';
import { schemaVersionLabel } from '../utils/scanSchemaVersion';
import { useTheme } from '../ThemeContext';
import DataSourceSelector from './DataSourceSelector';
import './TemplateSelectionPage.css';
import { apifetch } from '../services/interceptors';
// ─── Pro Template Definitions ────────────────────────────────────────────────
// These represent structured Pro-only report templates.
// They are static tiles shown to all users; Community users see an upgrade gate.
const PRO_TEMPLATES = [
    // Compliance & Frameworks
    { id: 'pro-hipaa', name: 'HIPAA Security Assessment', description: 'Evaluate HIPAA compliance posture with network security findings mapped to HIPAA controls.', category: 'compliance', icon: 'shield' },
    { id: 'pro-cmmc', name: 'CMMC Readiness Assessment', description: 'Assess CMMC 2.0 maturity level readiness based on infrastructure security controls.', category: 'compliance', icon: 'shield' },
    { id: 'pro-nist', name: 'NIST Cybersecurity Framework Assessment', description: 'Map scan findings to the NIST CSF framework across Identify, Protect, Detect, Respond, Recover.', category: 'compliance', icon: 'shield' },
    { id: 'pro-pci', name: 'PCI-DSS Readiness Review', description: 'Evaluate network segmentation, encryption, and access control against PCI-DSS requirements.', category: 'compliance', icon: 'shield' },

    // Security Assessments
    { id: 'pro-executive-risk', name: 'Executive Risk Summary', description: 'High-level risk overview designed for C-suite stakeholders with key metrics and recommendations.', category: 'assessment', icon: 'chart' },
    { id: 'pro-detailed-tech', name: 'Detailed Technical Assessment', description: 'Comprehensive technical deep-dive into findings, vulnerabilities, and remediation steps.', category: 'assessment', icon: 'chart' },
    { id: 'pro-presales', name: 'Pre-Sales Network Assessment', description: 'Prospect-ready assessment highlighting security gaps to drive MSP engagement conversations.', category: 'assessment', icon: 'chart' },
    { id: 'pro-cyber-insurance', name: 'Cyber Insurance Readiness Report', description: 'Evaluate readiness for cyber insurance applications with risk scoring and control evidence.', category: 'assessment', icon: 'chart' },
    { id: 'pro-m365', name: 'Microsoft 365 Security Assessment', description: 'Review M365 security configurations, MFA status, admin roles, and compliance posture.', category: 'assessment', icon: 'chart' },
    { id: 'pro-ad-health', name: 'Active Directory Health Audit', description: 'Audit AD structure, group policies, stale accounts, and privilege escalation risks.', category: 'assessment', icon: 'chart' },

    // Infrastructure & Operations
    { id: 'pro-backup-dr', name: 'Backup & Disaster Recovery Assessment', description: 'Assess backup coverage, RPO/RTO readiness, and disaster recovery plan gaps.', category: 'infrastructure', icon: 'server' },
    { id: 'pro-endpoint', name: 'Endpoint Protection Coverage Report', description: 'Map endpoint protection deployment across devices with coverage gap analysis.', category: 'infrastructure', icon: 'server' },
    { id: 'pro-encryption', name: 'Encryption & BitLocker Coverage Report', description: 'Evaluate disk encryption and BitLocker deployment status across all managed endpoints.', category: 'infrastructure', icon: 'server' },
    { id: 'pro-asset', name: 'Asset Lifecycle Report', description: 'Track device age, warranty status, and replacement recommendations across the network.', category: 'infrastructure', icon: 'server' },
    { id: 'pro-modernization', name: 'Infrastructure Modernization Plan', description: 'Strategic roadmap for upgrading legacy systems and modernizing IT infrastructure.', category: 'infrastructure', icon: 'server' },

    // Business & Management
    { id: 'pro-qbr', name: 'Quarterly Business Review (QBR)', description: 'Structured QBR template with service metrics, SLA performance, and strategic recommendations.', category: 'business', icon: 'building' },
    { id: 'pro-onboarding', name: 'Managed Services Onboarding Report', description: 'New client onboarding assessment with baseline findings and priority remediation plan.', category: 'business', icon: 'building' },
    { id: 'pro-monthly', name: 'Monthly Service Review', description: 'Monthly service performance summary with ticket metrics, patch status, and security highlights.', category: 'business', icon: 'building' },
    { id: 'pro-budget', name: 'IT Budget Forecast Report', description: 'Technology budget projection based on asset lifecycle, licensing, and infrastructure needs.', category: 'business', icon: 'building' },
    { id: 'pro-tco', name: 'Total Cost of Ownership Report', description: 'Comprehensive TCO analysis covering hardware, software, support, and operational costs.', category: 'business', icon: 'building' },

    // Strategy & Advanced
    { id: 'pro-maturity', name: 'IT Maturity Assessment', description: 'Benchmark IT operations maturity across people, process, and technology dimensions.', category: 'security', icon: 'cog' },
    { id: 'pro-roadmap', name: 'Technology Roadmap Report', description: 'Multi-phase technology improvement roadmap with prioritized initiatives and timelines.', category: 'security', icon: 'cog' },
    { id: 'pro-incident', name: 'Incident Response Summary', description: 'Post-incident analysis report with timeline, impact assessment, and lessons learned.', category: 'security', icon: 'cog' },
    { id: 'pro-ransomware', name: 'Ransomware Readiness Assessment', description: 'Evaluate ransomware resilience across backup, endpoint, network, and user awareness controls.', category: 'security', icon: 'cog' },
];

const CATEGORY_META = {
    compliance: { label: 'Compliance & Frameworks', color: '#059669', bgLight: '#ecfdf5', bgDark: '#064e3b' },
    assessment: { label: 'Security Assessments', color: '#2563eb', bgLight: '#eff6ff', bgDark: '#1e3a5f' },
    infrastructure: { label: 'Infrastructure & Operations', color: '#0891b2', bgLight: '#ecfeff', bgDark: '#164e63' },
    business: { label: 'Business & Management', color: '#7c3aed', bgLight: '#f5f3ff', bgDark: '#3b1f6e' },
    security: { label: 'Strategy & Advanced', color: '#dc2626', bgLight: '#fef2f2', bgDark: '#5c1a1a' },
};

const CATEGORY_ICONS = {
    compliance: FaShieldAlt,
    assessment: FaChartBar,
    infrastructure: FaServer,
    business: FaBuilding,
    security: FaCog,
};

function TemplateSelectionPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { scanFilePath, scanData, mode = null, organizationId = null } = location.state || {};
    const { isDarkMode } = useTheme();

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [licenseType, setLicenseType] = useState('community'); // default lower tier
    // Default to 'report' mode if no mode is provided (since mode selection is now in Export modal)
    const [builderMode] = useState(mode || 'report'); // 'template' or 'report'

    // Upgrade modal state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [clickedProTemplate, setClickedProTemplate] = useState(null);

    // Step-based navigation: Step 1 = Data Source Selection, Step 2 = Template Selection
    // In template mode, skip data source selection (step 1)
    // When coming from PastScans, still show Step 1 but with internal scan pre-selected
    const [currentStep, setCurrentStep] = useState(builderMode === 'template' ? 2 : 1);

    // Selected data sources from DataSourceSelector
    // CRITICAL: Pre-populate internal scan if coming from PastScans with scanFilePath
    const [selectedDataSources, setSelectedDataSources] = useState({
        internalScan: null,
        externalScan: null
    });


    useEffect(() => {
        loadTemplates();
    }, []);

    // Templates only render the scan format they were built for, so once a scan is chosen
    // ask the backend which templates fit its scanner version (v1 vs v2 JSON).
    // allowedIds stays null (no filtering) in template mode or if the check fails.
    const [scanCompat, setScanCompat] = useState({ version: null, allowedIds: null, hidden: 0 });
    const compatScanPath = builderMode === 'template'
        ? null
        : (selectedDataSources.internalScan?.file_path || scanFilePath || selectedDataSources.externalScan?.file_path || null);

    useEffect(() => {
        if (!compatScanPath) {
            setScanCompat({ version: null, allowedIds: null, hidden: 0 });
            return;
        }
        let cancelled = false;
        getTemplatesForScan(compatScanPath)
            .then(({ templates: fitting, scanSchemaVersion, hiddenCount }) => {
                if (cancelled) return;
                setScanCompat({
                    version: scanSchemaVersion,
                    allowedIds: new Set(fitting.map((t) => String(t.id))),
                    hidden: hiddenCount,
                });
            })
            .catch((err) => {
                console.error('[TemplateSelectionPage] Could not check template/scan compatibility:', err);
                if (!cancelled) setScanCompat({ version: null, allowedIds: null, hidden: 0 });
            });
        return () => { cancelled = true; };
    }, [compatScanPath]);

    const loadTemplates = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch subscription tier first
            const token = localStorage.getItem('token');
            try {
                const subscriptionResponse = await apifetch('/s1/api/subscription', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (subscriptionResponse.ok) {
                    const subscriptionData = await subscriptionResponse.json();
                    setLicenseType(subscriptionData.is_pro ? 'pro' : 'community');
                }
            } catch (err) {
                console.error('Error fetching subscription in TemplateSelectionPage:', err);
            }

            const fetchedTemplates = await getAvailableTemplates();

            // Remove duplicates based on template name
            const seenNames = new Set();
            const uniqueTemplates = fetchedTemplates.filter(template => {
                const normalizedName = template.name?.toLowerCase().trim();
                if (!normalizedName || seenNames.has(normalizedName)) {
                    return false;
                }
                seenNames.add(normalizedName);
                return true;
            });

            setTemplates(uniqueTemplates);
        } catch (err) {
            setError(err.message);
            console.error('Error loading templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
    };

    const handleBlankLayoutSelect = () => {
        setSelectedTemplate({ id: 'blank', name: 'Blank Layout' });
    };

    // Handle Upgrade to Pro click — in-app Stripe checkout.
    const handleUpgradeClick = () => {
        startProCheckout();
    };

    // Handle Pro template click
    const handleProTemplateClick = (proTemplate) => {
        if (licenseType === 'pro') {
            // Pro users: select and continue directly
            setSelectedTemplate({ id: proTemplate.id, name: proTemplate.name, isPro: true });
        } else {
            // Community users: show upgrade modal
            setClickedProTemplate(proTemplate);
            setShowUpgradeModal(true);
        }
    };

    // Handle data source selection from Step 1
    const handleDataSourceContinue = (dataSources) => {
        console.log('[TemplateSelectionPage] Data sources selected:', dataSources);
        setSelectedDataSources(dataSources);
        setCurrentStep(2); // Move to template selection
    };

    // Handle back from template selection to data source selection
    const handleBackToDataSources = () => {
        setCurrentStep(1);
    };

    const handleContinue = (templateOverride = null) => {
        const templateToUse = (templateOverride && templateOverride.id) ? templateOverride : selectedTemplate;
        if (!templateToUse) return;

        // Build file URLs based on selected data sources
        const internalScanPath = selectedDataSources.internalScan?.file_path || scanFilePath;
        const externalScanPath = selectedDataSources.externalScan?.file_path || null;

        // Normalize template ID using utility function
        const normalizedTemplateId = templateToUse.id === 'blank' 
            ? null 
            : normalizeTemplateId(templateToUse);
        
        console.log('[TemplateSelectionPage] Navigating to ReportBuilder:', {
            originalTemplateId: templateToUse.id,
            normalizedTemplateId: normalizedTemplateId,
            templateName: templateToUse.name
        });

        // Navigate to Report Builder with selected template and data sources
        navigate('/report-builder', {
            state: {
                fileUrl: internalScanPath, // Primary file URL (for backward compatibility)
                internalScanPath: internalScanPath,
                externalScanPath: externalScanPath,
                scanData: builderMode === 'template' ? null : scanData, // No scan data for template mode
                templateId: normalizedTemplateId,
                fromTemplateSelection: true,
                mode: builderMode, // 'report' or 'template'
                hasExternalData: !!externalScanPath,
                hasInternalData: !!internalScanPath
            }
        });
    };

    // Launch a blank canvas in Report Builder with the "Generate with AI" dialog
    // opened automatically. Mirrors handleContinue's data-source plumbing.
    const handleGenerateWithAI = () => {
        if (licenseType !== 'pro') {
            setClickedProTemplate({ id: 'blank', name: 'Generate with AI', description: 'Describe your report and let AI build the layout, charts, and narrative for you.' });
            setShowUpgradeModal(true);
            return;
        }

        const internalScanPath = selectedDataSources.internalScan?.file_path || scanFilePath;
        const externalScanPath = selectedDataSources.externalScan?.file_path || null;

        navigate('/report-builder', {
            state: {
                fileUrl: internalScanPath,
                internalScanPath: internalScanPath,
                externalScanPath: externalScanPath,
                scanData: builderMode === 'template' ? null : scanData,
                templateId: null, // blank canvas
                fromTemplateSelection: true,
                mode: builderMode,
                hasExternalData: !!externalScanPath,
                hasInternalData: !!internalScanPath,
                autoOpenAI: true
            }
        });
    };

    const handleBack = () => {
        // If on template selection step AND not from PastScans AND not in template mode
        // Then go back to data source selection
        if (currentStep === 2 && !scanFilePath && builderMode !== 'template') {
            // Go back to data source selection
            handleBackToDataSources();
        } else {
            // Otherwise go back to dashboard
            navigate('/dashboard');
        }
    };

    const handleCreateTemplate = () => {
        // Navigate to Report Builder in template creation mode - BLANK, no template selection
        navigate('/report-builder', {
            state: {
                mode: 'template',
                scanData: null, // No scan data for template mode
                fromTemplateSelection: true
            }
        });
    };



    // Filter templates based on search query
    const filteredTemplates = templates.filter(template => {
        // Apply search filter
        const matchesSearch = template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            template.description?.toLowerCase().includes(searchQuery.toLowerCase());

        const fitsScan = !scanCompat.allowedIds || scanCompat.allowedIds.has(String(template.id));

        return matchesSearch && fitsScan;
    });

    // Filter pro templates based on search
    const filteredProTemplates = PRO_TEMPLATES.filter(pt =>
        pt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pt.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group pro templates by category
    const proByCategory = {};
    filteredProTemplates.forEach(pt => {
        if (!proByCategory[pt.category]) proByCategory[pt.category] = [];
        proByCategory[pt.category].push(pt);
    });

    const defaultTemplates = filteredTemplates.filter(t => t.is_default);
    
    // Non-default templates should include both templates with is_default false and custom_ prefixed ones
    const nonDefaultTemplates = filteredTemplates.filter(t => 
        !t.is_default || String(t.id).startsWith('custom_')
    );

    const getTemplateIcon = (template) => {
        if (template.is_default) {
            return <FaCrown className="text-warning" />;
        }
        if (template.isCustom || String(template.id).startsWith('custom_')) {
            return <FaStar className="text-success" />;
        }
        return <FaStar className="text-primary" />;
    };

    const getTemplateLabel = (template) => {
        if (template.is_default) return 'Default';
        if (template.isCustom || String(template.id).startsWith('custom_')) {
            return 'Custom';
        }
        return 'Template';
    };

    // Templates produced by the AI Build feature carry an aiGenerated flag in their
    // configuration metadata; the AI save path also prefixes the description.
    const isAiGenerated = (template) => {
        if (!template) return false;
        if (template.ai_generated || template.aiGenerated) return true;
        const meta = template.configuration?.metadata || template.metadata;
        if (meta?.aiGenerated) return true;
        return /^ai[-\s]?generated/i.test(template.description || '');
    };

    // Helper function to extract widgets and findings from template
    const getTemplateInfo = (template) => {
        // For custom ReportBuilder templates (custom_ prefix), we don't have full data
        if (String(template.id).startsWith('custom_')) {
            return {
                widgets: ['Custom widgets'],
                findings: ['Custom findings'],
                hasData: false
            };
        }

        // Extract from configuration.visualTemplate.pages
        const config = template.configuration;
        if (!config || !config.visualTemplate || !config.visualTemplate.pages) {
            // Fallback for predefined templates or templates without visualTemplate
            return {
                widgets: [],
                findings: [],
                hasData: false
            };
        }

        const pages = config.visualTemplate.pages || [];
        const widgets = new Set();
        const findings = new Set();

        // Widget types that are considered "findings"
        const findingsWidgetTypes = [
            'findings-status', 'key-findings', 'findings-grid', 'open-ports',
            'open-ports-summary', 'darkweb-summary', 'accounts-summary',
            'accounts-admin-summary', 'firewall-summary', 'localhost-firewall-findings-summary',
            'localhost-service-risk-summary', 'service-risk-summary',
            'network-firewall-findings-summary', 'network-service-risk-summary',
            'admins-finding-summary', 'accounts-finding-summary',
            'backups-summary', 'overall-risk-score', 'nested-rectangle-findings'
        ];

        // Extract widgets from all pages
        pages.forEach(page => {
            if (page.blocks && Array.isArray(page.blocks)) {
                page.blocks.forEach(block => {
                    if (block.type === 'widget' && block.widgetType) {
                        const widgetType = block.widgetType;
                        widgets.add(widgetType);

                        // Check if it's a findings widget
                        if (findingsWidgetTypes.some(fwt => widgetType.includes(fwt) || widgetType === fwt)) {
                            findings.add(widgetType);
                        }
                    }
                });
            }
        });

        // Convert to arrays and format for display
        const widgetList = Array.from(widgets);
        const findingsList = Array.from(findings);

        // Widget type to display name mapping
        const widgetDisplayNames = {
            'overall-risk-score': 'Overall Risk Score',
            'key-findings': 'Key Findings',
            'findings-status': 'Findings Status',
            'findings-grid': 'Findings Grid',
            'open-ports': 'Open Ports',
            'open-ports-summary': 'Open Ports Summary',
            'darkweb-summary': 'Dark Web Summary',
            'accounts-summary': 'Accounts Summary',
            'accounts-admin-summary': 'Admin Accounts Summary',
            'firewall-summary': 'Firewall Summary',
            'localhost-firewall-findings-summary': 'Localhost Firewall Findings',
            'localhost-service-risk-summary': 'Localhost Service Risk',
            'service-risk-summary': 'Service Risk Summary',
            'network-firewall-findings-summary': 'Network Firewall Findings',
            'network-service-risk-summary': 'Network Service Risk',
            'admins-finding-summary': 'Admins Finding Summary',
            'accounts-finding-summary': 'Accounts Finding Summary',
            'backups-summary': 'Backups Summary',
            'nested-rectangle-findings': 'Nested Rectangle Findings',
            'category-description': 'Category Description',
            'financial-impact': 'Financial Impact',
            'company-at-a-glance': 'Company At A Glance',
            'category-score-bar': 'Category Score Bar'
        };

        // Format widget names for display
        const formatWidgetName = (widgetType) => {
            // Use display name if available, otherwise format from widget type
            if (widgetDisplayNames[widgetType]) {
                return widgetDisplayNames[widgetType];
            }
            // Fallback: format from widget type
            return widgetType
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };

        return {
            widgets: widgetList.map(formatWidgetName),
            findings: findingsList.map(formatWidgetName),
            allWidgets: widgetList.map(formatWidgetName), // All widgets including findings
            hasData: widgetList.length > 0
        };
    };

    // Helper to format template description
    const getTemplateDescription = (template) => {
        const info = getTemplateInfo(template);

        // For custom ReportBuilder templates, show generic message
        if (String(template.id).startsWith('custom_')) {
            return 'Custom template with drag-and-drop widgets';
        }

        // If no widget data available, fall back to description
        if (!info.hasData) {
            return template.description || 'Template with customizable widgets';
        }

        const parts = [];

        // Show findings first if any
        if (info.findings.length > 0) {
            // Limit to first 3 findings for display, add "and X more" if more exist
            const displayFindings = info.findings.slice(0, 3);
            let findingsText = displayFindings.join(', ');
            if (info.findings.length > 3) {
                findingsText += `, and ${info.findings.length - 3} more`;
            }
            parts.push(`Findings: ${findingsText}`);
        }

        // Show other widgets (non-findings)
        const nonFindingWidgets = info.widgets.filter(w => !info.findings.includes(w));
        if (nonFindingWidgets.length > 0) {
            // Limit to first 3 widgets for display
            const displayWidgets = nonFindingWidgets.slice(0, 3);
            let widgetsText = displayWidgets.join(', ');
            if (nonFindingWidgets.length > 3) {
                widgetsText += `, and ${nonFindingWidgets.length - 3} more`;
            }
            parts.push(`Widgets: ${widgetsText}`);
        }

        // If we only have findings widgets, show them as widgets
        if (parts.length === 0 && info.findings.length > 0) {
            const displayFindings = info.findings.slice(0, 4);
            let findingsText = displayFindings.join(', ');
            if (info.findings.length > 4) {
                findingsText += `, and ${info.findings.length - 4} more`;
            }
            return findingsText;
        }

        if (parts.length === 0) {
            return template.description || 'Template with widgets';
        }

        // Join with line break for better readability
        return parts.join(' • ');
    };

    // ─── Category grouping (AI report generation is the primary/default mode) ───
    // 1) AI-generated templates, admin defaults first; 2) non-AI templates, admin
    //    defaults first. The Blank "Create Template" card is rendered separately on top.
    const sortDefaultFirst = (a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0);
    const isCustomTpl = (t) => t.isCustom || String(t.id).startsWith('custom_');
    // A template is visible to Community users only if it's an admin default; custom
    // (non-default) templates remain Pro-gated, matching the previous behavior.
    const visibleForLicense = (t) => t.is_default || licenseType === 'pro';
    const aiTemplates = filteredTemplates.filter(t => isAiGenerated(t) && visibleForLicense(t)).sort(sortDefaultFirst);
    const nonAiTemplates = filteredTemplates.filter(t => !isAiGenerated(t) && visibleForLicense(t)).sort(sortDefaultFirst);

    // Reusable template card (used by every category so they look identical).
    const renderTemplateCard = (template, keyPrefix = 'tpl') => (
        <Col xs={12} sm={6} md={4} lg={3} key={`${keyPrefix}-${template.id}`}>
            <Card
                className={`template-preview-card ${isDarkMode ? 'dark-card' : ''} ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                onClick={() => handleTemplateSelect(template)}
                onDoubleClick={() => handleContinue(template)}
            >
                <div className={`template-preview ${isDarkMode ? 'dark-preview' : ''}`}>
                    <div className="template-thumbnail">
                        <div className="template-icon-large">{getTemplateIcon(template)}</div>
                        <div className="template-name-overlay">{template.name}</div>
                    </div>
                </div>
                <Card.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <Card.Title className={`template-card-title ${isDarkMode ? 'text-light' : ''}`}>
                            {template.name}
                        </Card.Title>
                        <div className="d-flex gap-1 flex-shrink-0">
                            {isAiGenerated(template) && (<span className="template-ai-badge">✨ AI</span>)}
                            <span className={`template-badge ${isDarkMode ? 'dark-badge' : ''}`}>
                                {getTemplateLabel(template)}
                            </span>
                        </div>
                    </div>
                    <Card.Text className={isDarkMode ? 'text-light opacity-75 small' : 'text-muted small'}>
                        {getTemplateDescription(template)}
                    </Card.Text>
                </Card.Body>
                {selectedTemplate?.id === template.id && (
                    <div className="selected-overlay">
                        <BiCheckCircle size={32} className="text-success" />
                    </div>
                )}
            </Card>
        </Col>
    );

    // Get the icon component for a Pro template
    const getProTemplateIcon = (proTemplate) => {
        const iconMap = {
            shield: FaShieldAlt,
            chart: FaChartBar,
            server: FaServer,
            building: FaBuilding,
            cog: FaCog,
        };
        const IconComp = iconMap[proTemplate.icon] || FaFileAlt;
        return <IconComp />;
    };

    return (
        <div className={`template-selection-page ${isDarkMode ? 'dark-mode' : ''}`}>
            {/* Step 1: Data Source Selection (only in report mode) */}
            {currentStep === 1 && builderMode !== 'template' && (
                <>
                    {/* Header for Step 1 */}
                    <div className={`template-header ${isDarkMode ? 'dark-header' : ''}`}>
                        <Container>
                            <div className="d-flex justify-content-between align-items-center py-3">
                                <div className="d-flex align-items-center">
                                    <Button
                                        variant={isDarkMode ? "outline-light" : "outline-secondary"}
                                        size="sm"
                                        onClick={() => navigate('/dashboard')}
                                        className="me-3"
                                    >
                                        <FaArrowLeft className="me-2" />
                                        Back
                                    </Button>
                                    <h4 className={`mb-0 ${isDarkMode ? 'text-light' : ''}`}>
                                        Create Custom Report
                                    </h4>
                                </div>
                                {/* Step Indicator */}
                                <div className="d-flex align-items-center">
                                    <Badge bg="primary" className="me-2 px-3 py-2">
                                        Step 1 of 2
                                    </Badge>
                                    <span className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                        Select Data Sources
                                    </span>
                                </div>
                            </div>
                        </Container>
                    </div>

                    {/* Data Source Selector */}
                    <DataSourceSelector
                        onContinue={handleDataSourceContinue}
                        organizationId={organizationId}
                        preSelectedScanPath={scanFilePath}
                        preSelectedScanData={scanData}
                    />
                </>
            )}

            {/* Step 2: Template Selection */}
            {(currentStep === 2 || builderMode === 'template') && (
                <>
                    {/* Header for Step 2 */}
                    <div className={`template-header ${isDarkMode ? 'dark-header' : ''}`}>
                        <Container>
                            <div className="d-flex justify-content-between align-items-center py-3">
                                <div className="d-flex align-items-center">
                                    <Button
                                        variant={isDarkMode ? "outline-light" : "outline-secondary"}
                                        size="sm"
                                        onClick={handleBack}
                                        className="me-3"
                                    >
                                        <FaArrowLeft className="me-2" />
                                        Back
                                    </Button>
                                    <h4 className={`mb-0 ${isDarkMode ? 'text-light' : ''}`}>
                                        {builderMode === 'template' ? 'Create New Template' : 'Select Report Template'}
                                    </h4>
                                    <span className={`badge ${isDarkMode ? 'bg-info' : 'bg-info'} ms-3`}>
                                        {builderMode === 'template' ? 'Template Mode' : 'Report Mode'}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center">
                                    {/* Step Indicator (only in report mode) */}
                                    {builderMode !== 'template' && (
                                        <>
                                            <Badge bg="primary" className="me-2 px-3 py-2">
                                                Step 2 of 2
                                            </Badge>
                                            <span className={`me-3 ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
                                                Choose Template
                                            </span>
                                        </>
                                    )}
                                    {builderMode === 'template' && (
                                        <Button
                                            variant="success"
                                            onClick={handleCreateTemplate}
                                            className="d-flex align-items-center"
                                        >
                                            <FaPlus className="me-2" />
                                            Start from Blank
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Container>
                    </div>

                    {/* Selected Data Sources Summary (only in report mode) */}
                    {builderMode !== 'template' && (selectedDataSources.internalScan || selectedDataSources.externalScan) && (
                        <div className={`py-2 ${isDarkMode ? 'bg-dark border-bottom border-secondary' : 'bg-light border-bottom'}`}>
                            <Container>
                                <div className="d-flex align-items-center">
                                    <span className={`me-2 small ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
                                        <strong>Data Sources:</strong>
                                    </span>
                                    {selectedDataSources.internalScan && (
                                        <Badge bg="primary" className="me-2">
                                            Internal Scan
                                        </Badge>
                                    )}
                                    {selectedDataSources.externalScan && (
                                        <Badge bg="info">
                                            External Scan: {selectedDataSources.externalScan.domain || 'Domain'}
                                        </Badge>
                                    )}
                                    {scanCompat.version && (
                                        <Badge bg="secondary" className="ms-2" title="Only templates built for this scan format are listed">
                                            Scanner {schemaVersionLabel(scanCompat.version)}
                                        </Badge>
                                    )}
                                    {scanCompat.hidden > 0 && (
                                        <span className={`ms-2 small ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
                                            {scanCompat.hidden} template{scanCompat.hidden === 1 ? '' : 's'} for other scanner versions hidden
                                        </span>
                                    )}
                                </div>
                            </Container>
                        </div>
                    )}

                    {/* Search Bar */}
                    <div className={`template-search-section py-3 ${isDarkMode ? 'dark-search' : 'bg-light'}`}>
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={6}>
                                    <InputGroup>
                                        <InputGroup.Text className={isDarkMode ? 'bg-dark border-secondary text-light' : ''}>
                                            <FaSearch />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search templates..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                                            style={isDarkMode ? {
                                                backgroundColor: '#2d2d2d',
                                                color: '#ffffff',
                                                borderColor: '#555'
                                            } : {}}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Container>
                    </div>

                    {/* Main Content */}
                    <Container className="py-4">
                        {loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" size="lg" />
                                <p className={`mt-3 ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
                                    Loading templates...
                                </p>
                            </div>
                        )}

                        {error && (
                            <Alert variant="danger" className={isDarkMode ? 'bg-dark border-danger' : ''}>
                                <Alert.Heading className={isDarkMode ? 'text-light' : ''}>
                                    Error Loading Templates
                                </Alert.Heading>
                                <p className={isDarkMode ? 'text-light' : ''}>{error}</p>
                                <Button variant="outline-danger" size="sm" onClick={loadTemplates}>
                                    Retry
                                </Button>
                            </Alert>
                        )}

                        {!loading && !error && (
                            <>
                                {/* ─── 1) Create Template (Blank canvas) ─── */}
                                <div className="template-section-header mb-3">
                                    <h6 className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                        <FaPlus className="text-success me-2" />
                                        Create Template
                                    </h6>
                                    <hr className={isDarkMode ? 'border-secondary' : ''} />
                                </div>
                                <Row className="g-4 mb-4">
                                    {/* Generate with AI — opens a blank canvas with the AI dialog auto-opened */}
                                    <Col xs={12} sm={6} md={4} lg={3}>
                                        <Card
                                            className={`template-preview-card generate-ai-card ${isDarkMode ? 'dark-card' : ''} ${licenseType !== 'pro' ? 'gated' : ''}`}
                                            onClick={handleGenerateWithAI}
                                        >
                                            {licenseType !== 'pro' && (
                                                <div className="pro-badge-overlay">
                                                    <FaCrown /> PRO
                                                </div>
                                            )}
                                            <div className={`template-preview ${isDarkMode ? 'dark-preview' : ''}`}>
                                                <div className="generate-ai-preview">
                                                    <FaMagic size={44} className="text-white" />
                                                </div>
                                            </div>
                                            <Card.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
                                                <Card.Title className={`template-card-title ${isDarkMode ? 'text-light' : ''}`}>
                                                    ✨ Generate with AI
                                                </Card.Title>
                                                <Card.Text className={isDarkMode ? 'text-light opacity-75 small' : 'text-muted small'}>
                                                    Describe your report and let AI build the layout for you
                                                </Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* Blank Layout — full manual drag-and-drop control */}
                                    <Col xs={12} sm={6} md={4} lg={3}>
                                        <Card
                                            className={`template-preview-card ${isDarkMode ? 'dark-card' : ''} ${licenseType !== 'pro' ? 'gated' : ''} ${selectedTemplate?.id === 'blank' ? 'selected' : ''}`}
                                            onClick={() => {
                                                if (licenseType === 'pro') {
                                                    handleBlankLayoutSelect();
                                                } else {
                                                    setClickedProTemplate({ id: 'blank', name: 'Blank Layout', description: 'Start from scratch with a blank canvas and full drag-and-drop design control.' });
                                                    setShowUpgradeModal(true);
                                                }
                                            }}
                                            onDoubleClick={() => {
                                                if (licenseType === 'pro') handleContinue({ id: 'blank', name: 'Blank Layout' });
                                            }}
                                        >
                                            {licenseType !== 'pro' && (
                                                <div className="pro-badge-overlay">
                                                    <FaCrown /> PRO
                                                </div>
                                            )}
                                            <div className={`template-preview ${isDarkMode ? 'dark-preview' : ''}`}>
                                                <div className={`blank-template-preview ${isDarkMode ? 'dark-blank' : ''}`}>
                                                    <FaPlus size={48} className={isDarkMode ? 'text-light opacity-50' : 'text-muted'} />
                                                </div>
                                            </div>
                                            <Card.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
                                                <Card.Title className={`template-card-title ${isDarkMode ? 'text-light' : ''}`}>
                                                    Blank Layout
                                                </Card.Title>
                                                <Card.Text className={isDarkMode ? 'text-light opacity-75 small' : 'text-muted small'}>
                                                    Start from scratch with a blank canvas
                                                </Card.Text>
                                            </Card.Body>
                                            {selectedTemplate?.id === 'blank' && (
                                                <div className="selected-overlay">
                                                    <BiCheckCircle size={32} className="text-success" />
                                                </div>
                                            )}
                                        </Card>
                                    </Col>
                                </Row>

                                {/* ─── 2) AI-Generated Reports (primary mode) ─── */}
                                {aiTemplates.length > 0 && (
                                    <>
                                        <div className="template-section-header mt-4 mb-3">
                                            <h6 className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                                <span className="me-2">✨</span>
                                                AI-Generated Reports <Badge bg="secondary" className="ms-2">{aiTemplates.length}</Badge>
                                            </h6>
                                            <hr className={isDarkMode ? 'border-secondary' : ''} />
                                        </div>
                                        <Row className="g-4 mb-4">
                                            {aiTemplates.map((template) => renderTemplateCard(template, 'ai'))}
                                        </Row>
                                    </>
                                )}

                                {/* ─── 3) Report Templates (non-AI) ─── */}
                                {nonAiTemplates.length > 0 && (
                                    <>
                                        <div className="template-section-header mt-4 mb-3">
                                            <h6 className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                                <FaCrown className="text-warning me-2" />
                                                Report Templates <Badge bg="secondary" className="ms-2">{nonAiTemplates.length}</Badge>
                                            </h6>
                                            <hr className={isDarkMode ? 'border-secondary' : ''} />
                                        </div>
                                        <Row className="g-4 mb-4">
                                            {nonAiTemplates.map((template) => renderTemplateCard(template, 'std'))}
                                        </Row>
                                    </>
                                )}

                                {/* ─── Pro Templates Section ─── */}
                                {licenseType === 'community' && (
                                    <>
                                        <hr className="pro-section-divider" />

                                        <div className="template-section-header mb-3">
                                            <h6 className={isDarkMode ? 'text-light' : ''}>
                                                <FaCrown className="text-warning me-2" />
                                                Pro Report Templates <Badge bg="warning" text="dark" className="ms-2">{filteredProTemplates.length}</Badge>
                                            </h6>
                                            <hr className={isDarkMode ? 'border-secondary' : ''} />
                                        </div>

                                        <Alert variant="info" className={`mb-4 ${isDarkMode ? 'bg-dark border-info' : ''}`}>
                                            <FaLock className="me-2" />
                                            <span className={isDarkMode ? 'text-light' : ''}>
                                                {/* These structured report templates are available with <strong className="pro-highlight">Pipsqueak Pro</strong>. Click any template to learn more about upgrading. */}
                                                The ability to create structured report templates is available with <strong className="pro-highlight">Pipsqueak Pro</strong>. Click any template to learn more about upgrading. 
                                            </span>
                                        </Alert>

                                        {/* Render Pro templates grouped by category */}
                                        {Object.entries(proByCategory).map(([category, categoryTemplates]) => {
                                            const meta = CATEGORY_META[category];
                                            const CategoryIcon = CATEGORY_ICONS[category] || FaFileAlt;

                                            return (
                                                <div key={category} className="mb-4">
                                                    <div className="pro-category-header">
                                                        <div 
                                                            className="category-icon" 
                                                            style={{ 
                                                                backgroundColor: isDarkMode ? meta.bgDark : meta.bgLight, 
                                                                color: meta.color 
                                                            }}
                                                        >
                                                            <CategoryIcon />
                                                        </div>
                                                        <h6 className={isDarkMode ? 'text-light opacity-75' : 'text-muted'} style={{ fontSize: '0.85rem' }}>
                                                            {meta.label}
                                                        </h6>
                                                    </div>
                                                    <Row className="g-4 mb-3">
                                                        {categoryTemplates.map((proTemplate) => {
                                                            const isSelected = selectedTemplate?.id === proTemplate.id;
                                                            const isGated = licenseType !== 'pro';

                                                            return (
                                                                <Col xs={12} sm={6} md={4} lg={3} key={proTemplate.id}>
                                                                    <Card
                                                                        className={`template-preview-card ${isDarkMode ? 'dark-card' : ''} ${isGated ? 'gated' : ''} ${isSelected ? 'selected' : ''}`}
                                                                        onClick={() => handleProTemplateClick(proTemplate)}
                                                                        onDoubleClick={() => {
                                                                            if (licenseType === 'pro') handleContinue({ id: proTemplate.id, name: proTemplate.name, isPro: true });
                                                                        }}
                                                                    >
                                                                        {/* Pro Badge (visible for Community only) */}
                                                                        {isGated && (
                                                                            <div className="pro-badge-overlay">
                                                                                <FaCrown /> PRO
                                                                            </div>
                                                                        )}

                                                                        <div className={`template-preview ${isDarkMode ? 'dark-preview' : ''}`}>
                                                                            <div className={`template-thumbnail ${proTemplate.category}`}>
                                                                                <div className="template-icon-large">
                                                                                    {getProTemplateIcon(proTemplate)}
                                                                                </div>
                                                                                <div className="template-name-overlay">
                                                                                    {proTemplate.name}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <Card.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
                                                                            <Card.Title className={`template-card-title ${isDarkMode ? 'text-light' : ''}`}>
                                                                                {proTemplate.name}
                                                                            </Card.Title>
                                                                            <Card.Text className={isDarkMode ? 'text-light opacity-75 small' : 'text-muted small'}>
                                                                                {proTemplate.description}
                                                                            </Card.Text>
                                                                        </Card.Body>
                                                                        {isSelected && (
                                                                            <div className="selected-overlay">
                                                                                <BiCheckCircle size={32} className="text-success" />
                                                                            </div>
                                                                        )}
                                                                    </Card>
                                                                </Col>
                                                            );
                                                        })}
                                                    </Row>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}

                                {filteredTemplates.length === 0 && filteredProTemplates.length === 0 && searchQuery && (
                                    <div className="text-center py-5">
                                        <p className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                            No templates found matching "{searchQuery}"
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </Container>

                    {/* Footer with Continue Button */}
                    {selectedTemplate && (
                        <div className={`template-footer ${isDarkMode ? 'dark-footer' : ''}`}>
                            <Container>
                                <div className="d-flex justify-content-between align-items-center py-3">
                                    <div className={isDarkMode ? 'text-light' : ''}>
                                        <strong>Selected:</strong> {selectedTemplate.name}
                                    </div>
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        onClick={handleContinue}
                                    >
                                        Continue with {selectedTemplate.name}
                                    </Button>
                                </div>
                            </Container>
                        </div>
                    )}
                </>
            )}

            {/* ─── Upgrade Funnel Modal (Community Only) ─── */}
            <Modal 
                show={showUpgradeModal} 
                onHide={() => setShowUpgradeModal(false)} 
                centered 
                size="lg"
                backdrop="static"
                className={`upgrade-modal ${isDarkMode ? 'dark-upgrade' : ''}`}
            >
                <Modal.Header closeButton>
                    <Modal.Title>🔓 Unlock Pro Templates</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {clickedProTemplate && (
                        <div className="mb-3">
                            <p className={isDarkMode ? 'text-light opacity-75' : 'text-muted'} style={{ fontSize: '0.9rem' }}>
                                You selected:
                            </p>
                            <h5 className={isDarkMode ? 'text-light' : ''}>
                                <FaCrown className="text-warning me-2" />
                                {clickedProTemplate.name}
                            </h5>
                            <p className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                {clickedProTemplate.description}
                            </p>
                        </div>
                    )}
                    
                    <hr className={isDarkMode ? 'border-secondary' : ''} />

                    <p className={isDarkMode ? 'text-light' : ''}>
                        <strong>Pipsqueak Pro</strong> gives you access to structured report templates designed for MSPs, vCISOs, and IT professionals:
                    </p>

                    <ul className="upgrade-feature-list">
                        <li>
                            <BiCheckCircle className="feature-icon" />
                            <span><strong>Structured QBR Reporting</strong> — Deliver professional quarterly business reviews with pre-built layouts and metrics</span>
                        </li>
                        <li>
                            <BiCheckCircle className="feature-icon" />
                            <span><strong>Compliance Frameworks</strong> — HIPAA, CMMC, NIST CSF, and PCI-DSS assessment templates with mapped controls</span>
                        </li>
                        <li>
                            <BiCheckCircle className="feature-icon" />
                            <span><strong>Build Your Own Reports (BYOR)</strong> — Custom template builder with drag-and-drop widgets and full design control</span>
                        </li>
                        <li>
                            <BiCheckCircle className="feature-icon" />
                            <span><strong>Editable Headers & Footers</strong> — Brand every report with your logo, colors, and custom header/footer content</span>
                        </li>
                        <li>
                            <BiCheckCircle className="feature-icon" />
                            <span><strong>Advanced Widgets</strong> — Financial impact analysis, infrastructure grids, category scoring, and more</span>
                        </li>
                        <li>
                            <BiCheckCircle className="feature-icon" />
                            <span><strong>24 Pre-Built Report Types</strong> — From executive summaries to ransomware readiness, instantly create professional reports</span>
                        </li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowUpgradeModal(false)}
                    >
                        Maybe Later
                    </Button>
                    <Button 
                        variant="warning"
                        onClick={handleUpgradeClick}
                        className="d-flex align-items-center"
                        style={{ fontWeight: '600' }}
                    >
                        <FaCrown className="me-2" />
                        Upgrade to Pro
                        <FaExternalLinkAlt className="ms-2" style={{ fontSize: '0.8rem' }} />
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default TemplateSelectionPage;

